import classNames from 'classnames';
import { memo, useCallback, useRef } from 'react';
import Button, { ButtonProps } from '@/components/molecules/button/button';
import SearchBarSection from '@/components/molecules/search-bar-section/search-bar-section';
import {
  LocationChangeParameters,
  RoomsState,
  SearchBarWrapperSectionData,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import variables from '@/components/organisms/search-bar/search-bar.variables';
import SearchBarBrandSelectorButton from '@/components/organisms/search-bar/shared/search-bar-brand/search-bar-brand-selector-button/search-bar-brand-selector-button';
import SearchBarBrandSelectorLedge from '@/components/organisms/search-bar/shared/search-bar-brand/search-bar-brand-selector-ledge/search-bar-brand-selector-ledge';
import { useSearchContext } from '@/libraries/contexts/search-context';
import useFocusIntoPanel from '@/libraries/hooks/use-focus-into-panel';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import SearchBarDropdown from '../search-bar-dropdown/search-bar-dropdown';
import styles from './search-bar-wrapper.module.scss';

export interface SearchBarWrapperProps {
  /** The active section id */
  activeSection?: number;
  /** Label to show in the button  */
  buttonLabel: string;
  /** Indicates whether the search bar is only buttons to open the widget modal  */
  buttonMode?: boolean;
  /** Indicates whether the search button is enabled  */
  canSubmit?: boolean;
  /** The class name to apply */
  className?: string;
  /** Indicates whether to show the compact version */
  compact?: boolean;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** Indicates whether the search bar is used in the campaign page */
  isInCampaign?: boolean;
  /** Indicates whether the date section is loading */
  isLoadingDate?: boolean;
  /** Indicates whether the locations section is loading */
  isLoadingLocations?: boolean;
  /** Indicates whether the search bar is used in the search widget */
  isWidget?: boolean;
  /** An event that is triggered on brand change */
  onChangeBrand?: (value: Content.Properties.Brand) => void;
  /** An event that is triggered on date change */
  onChangeDate?: (dates: Date[]) => void;
  /** An event that is triggered on duration change */
  onChangeDuration?: (id: string) => void;
  /** An event that is triggered on location change */
  onChangeLocations?: ({
    destinationRegions,
    destinations,
    hotels,
    locations,
  }: LocationChangeParameters) => void;
  /** An event that is triggered on persons change */
  onChangePersons?: (roomsState: RoomsState) => void;
  /** An event that is triggered on transportation change */
  onChangeTransportation?: (id: string) => void;
  /** An event that is triggered on click back */
  onClickBack?: () => void;
  /** An event that is triggered on close click */
  onClickClose?: () => void;
  /** An event that is triggered on next click */
  onClickNext?: () => void;
  /** An event that is triggered on close click */
  onClickSearch?: ButtonProps['onClick'];
  /** An event that is triggered on search bar section click */
  onClickSection?: (id: number) => void;
  /** The search settings */
  searchSettings?: Content.Components['SearchSettingsDto'];
  /** Sections to render in the search bar */
  sections?: SearchBarWrapperSectionData[];
}

const SearchBarWrapper = memo(
  ({
    activeSection = -1,
    buttonLabel,
    buttonMode = false,
    canSubmit = false,
    className,
    compact = false,
    customRangeStyle = false,
    isInCampaign = false,
    isLoadingDate = false,
    isLoadingLocations = false,
    isWidget = false,
    onChangeBrand,
    onChangeDate,
    onChangeDuration,
    onChangeLocations,
    onChangePersons,
    onChangeTransportation,
    onClickBack,
    onClickClose,
    onClickNext,
    onClickSearch,
    onClickSection,
    searchSettings,
    sections = [],
    ...rest
  }: SearchBarWrapperProps) => {
    const { brand } = useSearchContext();
    const componentRef = useRef<HTMLDivElement>(null);
    const sectionsRef = useRef<HTMLDivElement>(null);
    const isLaptop = useWindowSize('laptop');

    // Opening a section moves focus into its dropdown, past the other sections
    useFocusIntoPanel({
      activePanel: activeSection === -1 ? null : activeSection,
      containerRef: componentRef,
      // Tabbing past the end of a dropdown closes it and continues with the next
      // section. Not on mobile, where the dropdown covers the sections, or in
      // the widget, where it stays open
      onTabPastPanel: isLaptop && !isWidget ? onClickClose : undefined,
      triggersRef: sectionsRef,
    });

    const hasBrandSelectorLedge =
      !compact &&
      !isWidget &&
      !isInCampaign &&
      variables.showBrandSelectorLedge;
    const hasBrandSelectorSection =
      compact &&
      !isWidget &&
      !isInCampaign &&
      variables.showBrandSelectorSection &&
      sections?.some(
        section => section.type === SearchBarWrapperSectionType.BRAND,
      );

    const renderSection = useCallback(
      (section: SearchBarWrapperSectionData) => {
        const isBrandSelectorButtonWrapper =
          section.type === SearchBarWrapperSectionType.BRAND;

        const wrapperProps: Record<string, any> = {
          className: classNames(styles.searchSectionWrapper, {
            [styles.isActive]: section.id === activeSection,
            [styles.isBrandSelectorButtonWrapper]: isBrandSelectorButtonWrapper,
          }),
          ...(isBrandSelectorButtonWrapper && { 'data-brand': brand }),
        };

        return (
          <div key={section.id} {...wrapperProps}>
            {isBrandSelectorButtonWrapper ? (
              <SearchBarBrandSelectorButton
                className={styles.searchSection}
                disabled={section.disabled}
                isActive={section.id === activeSection}
                isNotified={section.isNotified}
                onClick={() => onClickSection?.(section.id)}
              />
            ) : (
              <SearchBarSection
                className={styles.searchSection}
                disabled={section.disabled}
                hideArrow
                isActive={section.id === activeSection}
                isLoading={section.isLoading}
                isNotified={section.isNotified}
                label={section.label}
                onClick={() => onClickSection && onClickSection(section.id)}
                placeholder={section.placeholder}
                value={section.value}
              />
            )}
          </div>
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [activeSection],
    );

    return (
      <div
        {...rest}
        ref={componentRef}
        className={classNames(styles.component, className, {
          [styles.hasFourSections]: sections.length === 4,
          [styles.hasThreeSections]: sections.length === 3,
          [styles.hasBrandSelectorLedge]: hasBrandSelectorLedge,
          [styles.hasBrandSelectorSection]: hasBrandSelectorSection,
          [styles.isActive]: activeSection !== -1,
          [styles.isCompact]: compact,
          [styles.isSearchWidget]: isWidget && !buttonMode,
        })}
      >
        {hasBrandSelectorLedge && (
          <SearchBarBrandSelectorLedge
            className={styles.brandSelectorLedge}
            onChange={onChangeBrand}
          />
        )}

        <div
          ref={sectionsRef}
          className={classNames(styles.searchBarWrapper, {
            [styles.isSearchWidget]: isWidget,
          })}
        >
          {/* Should be shown only for mobile */}
          {(hasBrandSelectorSection || hasBrandSelectorLedge) && (
            <div
              className={classNames(
                styles.searchSectionWrapper,
                styles.brandSelectorMobileWrapper,
              )}
            >
              <SearchBarBrandSelectorLedge
                hideConnectionCurve
                onChange={onChangeBrand}
              />
            </div>
          )}

          {sections?.map(renderSection)}

          {!isWidget && (
            <Button
              className={styles.button}
              disabled={!canSubmit}
              onClick={onClickSearch}
              text={buttonLabel}
            />
          )}
        </div>

        {!buttonMode && (
          <SearchBarDropdown
            activeSection={activeSection}
            customRangeStyle={customRangeStyle}
            isLoadingDate={isLoadingDate}
            isLoadingLocations={isLoadingLocations}
            isWidget={isWidget}
            onChangeBrand={onChangeBrand}
            onChangeDate={onChangeDate}
            onChangeDuration={onChangeDuration}
            onChangeLocations={onChangeLocations}
            onChangePersons={onChangePersons}
            onChangeTransportation={onChangeTransportation}
            onClickBack={onClickBack}
            onClickClose={onClickClose}
            onClickNext={onClickNext}
            onClickSearch={onClickSearch}
            searchSettings={searchSettings}
            sections={sections}
          />
        )}
      </div>
    );
  },
);

SearchBarWrapper.displayName = 'SearchBarWrapper';

export default SearchBarWrapper;
